// How a browser client ends up placed in a tenant: the sentinel it connects
// with, the connect token `<lane>:<tenant>:<inbox>:<credential>` it presents,
// the inbox token it mints once and keeps across every reconnect, and the
// credential it re-reads at every connect
// (02-DESIGN/01-identity/02-the-auth-service.md § the connect token and the
// lanes; 03-signing-in.md § connecting; decision 0006).
//
// No caller composes a connect token by hand: a Client does, and sets the
// inbox prefix before it connects.
import { jwtAuthenticator, tokenAuthenticator, } from "@nats-io/nats-core";
import { hexEncode, utf8Decode, utf8Encode } from "./encoding.js";
import { inbox as inboxPrefix, USER_INFO, validSlug } from "./subject.js";
/**
 * Lane names which credential a connect token carries. It is chosen by the
 * client and never inferred from the credential's shape.
 */
export const Lane = {
    oidc: "oidc",
    github: "github",
    app: "app",
    workload: "workload",
};
/** validLane reports whether s is one of the four lanes. */
export function validLane(s) {
    return s === Lane.oidc || s === Lane.github || s === Lane.app || s === Lane.workload;
}
/**
 * PLANE is the tenant field that asks for the identity plane: `-`, which is
 * not a slug, since a slug begins with a letter. On the platform provider's
 * lanes, `oidc` and `github`, the auth service then places the sign-in on the
 * plane whatever its memberships (decision 0072; 02-the-auth-service.md § the
 * connect token and the lanes).
 */
export const PLANE = "-";
/** planeLane reports whether a lane may ask for the identity plane. */
export function planeLane(lane) {
    return lane === Lane.oidc || lane === Lane.github;
}
/** formatToken renders `<lane>:<tenant>:<inbox>:<credential>`. */
export function formatToken(t) {
    return `${t.lane}:${t.tenant}:${t.inbox}:${t.credential}`;
}
/** ConnectError is a refusal of this library's own, with its reason. */
export class ConnectError extends Error {
    reason;
    constructor(reason, message, options) {
        super(message, options);
        this.name = "ConnectError";
        this.reason = reason;
    }
}
/**
 * parseToken splits a connect token and validates its lane, tenant and inbox.
 * The credential is not inspected.
 */
export function parseToken(s) {
    const parts = [];
    let rest = s;
    for (let i = 0; i < 3; i++) {
        const at = rest.indexOf(":");
        if (at < 0) {
            break;
        }
        parts.push(rest.slice(0, at));
        rest = rest.slice(at + 1);
    }
    parts.push(rest);
    if (parts.length !== 4) {
        throw new ConnectError("bad-shape", "connect: token does not have four colon-separated parts");
    }
    const [lane, tenant, inbox, credential] = parts;
    if (!validLane(lane)) {
        throw new ConnectError("bad-lane", "connect: unknown lane");
    }
    if (tenant === PLANE) {
        if (!planeLane(lane)) {
            throw new ConnectError("bad-tenant", "connect: the identity plane is asked for only on the oidc and github lanes");
        }
    }
    else if (tenant !== "" && !validSlug(tenant)) {
        throw new ConnectError("bad-tenant", "connect: tenant is not a slug");
    }
    if (!validInbox(inbox)) {
        throw new ConnectError("bad-inbox", "connect: inbox token missing, too short or not a subject token");
    }
    return { lane, tenant, inbox, credential };
}
/** INBOX_BITS is the randomness an inbox token carries. */
export const INBOX_BITS = 128;
/**
 * newInbox mints an inbox token: 128 bits of randomness as 32 lowercase hex
 * characters, so that it is a subject token and survives the tag's
 * lowercasing — the encoding the Go library mints.
 */
export function newInbox() {
    const b = new Uint8Array(INBOX_BITS / 8);
    crypto.getRandomValues(b);
    return hexEncode(b);
}
/**
 * validInbox reports whether s has the shape of an inbox token: at least 25
 * lowercase letters and digits (the shortest encoding of 128 bits), at most 64.
 */
export function validInbox(s) {
    return /^[a-z0-9]{25,64}$/.test(s);
}
// A decorated block: a dashed line, the value, a dashed line — as a .creds
// file holds the JWT and then the seed.
const block = /\s*(?:(?:[-]{3,}[^\n]*[-]{3,}\n)(.+)(?:\n\s*[-]{3,}[^\n]*[-]{3,}\n))/g;
/**
 * parseSentinel reads a sentinel from NATS credentials text (a decorated JWT
 * and seed, as a .creds file holds them, which is how the platform profile
 * carries it).
 */
export function parseSentinel(creds) {
    const text = typeof creds === "string" ? creds : utf8Decode(creds);
    const re = new RegExp(block.source, block.flags);
    const jwt = re.exec(text)?.[1]?.trim();
    if (jwt === undefined || jwt === "") {
        throw new ConnectError("sentinel", "connect: sentinel jwt: no decorated JWT in the credentials");
    }
    const seed = re.exec(text)?.[1]?.trim();
    if (seed === undefined || !seed.startsWith("SU")) {
        throw new ConnectError("sentinel", "connect: sentinel seed: no user seed in the credentials");
    }
    return { jwt, seed: utf8Encode(seed) };
}
/** staticCredential returns a credential that never changes: an app credential. */
export function staticCredential(s) {
    return () => s;
}
// The pace of reconnects (02-DESIGN/01-identity/02-the-auth-service.md
// § expiry, kick and revocation): every connect is a callout, so a
// connection that drops as soon as it is made must not become a callout
// every couple of seconds per tab. The Go library's connect.Backoff keeps
// the same pace.
/** RECONNECT_FIRST is the wait, in milliseconds, before the first attempt after a connection that stayed up. */
export const RECONNECT_FIRST = 500;
/** RECONNECT_MAX caps the wait, in milliseconds. */
export const RECONNECT_MAX = 30_000;
/** RECONNECT_STABLE is how long, in milliseconds, a connection stays up to count as stable, which resets the wait. */
export const RECONNECT_STABLE = 60_000;
/**
 * Backoff paces a connection's reconnects. The wait doubles from
 * RECONNECT_FIRST up to RECONNECT_MAX with every failed attempt and with
 * every connection that lived less than RECONNECT_STABLE; a connection that
 * lived longer resets it.
 *
 * nats-core's own wait is measured from the last dial, so a connection that
 * stayed up is redialled at once and one dropped at once waits a flat
 * interval. The backoff therefore turns that wait off and waits in the
 * server-selection hook instead, which the client calls once before every
 * dial with the pool as it stands: the server dialled last, its failed
 * attempts since it last connected and when that dial began.
 */
export class Backoff {
    jitter;
    now;
    short = 0; // connections in a row that lived less than RECONNECT_STABLE
    attempt = 0; // attempts since the last connection was lost
    constructor(jitter = (d) => Math.floor(Math.random() * (Math.floor(d / 5) + 1)), now = Date.now) {
        this.jitter = jitter;
        this.now = now;
    }
    /**
     * delay is the wait before reconnect attempt n (1-based). lived, the
     * lifetime of the connection just lost, is given with the first attempt
     * after it, and absent when no connection was lost (a first connect that
     * the client retries).
     */
    delay(attempt, lived) {
        if (lived !== undefined) {
            this.short = lived < RECONNECT_STABLE ? this.short + 1 : 0;
        }
        const steps = this.short + attempt - 1;
        const d = steps < 16 ? Math.min(RECONNECT_FIRST * 2 ** steps, RECONNECT_MAX) : RECONNECT_MAX;
        return d + this.jitter(d);
    }
    select = (pool) => {
        let last;
        for (const s of pool) {
            if (s.lastConnect > (last?.lastConnect ?? 0)) {
                last = s;
            }
        }
        const current = pool[0];
        if (last === undefined || current === undefined) {
            return null; // the first dial
        }
        let lived;
        if (last.didConnect && last.reconnects === 0) {
            // The last dial connected, and that connection is what was lost.
            this.attempt = 1;
            lived = this.now() - last.lastConnect;
        }
        else {
            this.attempt++;
        }
        return { server: current, delay: this.delay(this.attempt, lived) };
    };
    /**
     * options are the connection options that apply the backoff. A caller that
     * lays its own reconnectToServer or reconnectDelayHandler over them gives
     * up the pace.
     */
    options() {
        return { reconnectDelayHandler: () => 0, reconnectToServer: this.select };
    }
}
/**
 * Client composes the connect token and the connection options for one placed
 * connection. The inbox is minted once and kept across every reconnect.
 */
export class Client {
    sentinel;
    lane;
    /** The tenant field the token carries: a slug, PLANE, or empty. */
    tenant;
    inbox;
    name;
    credential;
    onError;
    constructor(o) {
        if (!validLane(o.lane)) {
            throw new ConnectError("bad-lane", "connect: unknown lane");
        }
        let tenant = o.tenant ?? "";
        if (o.plane === true) {
            if (tenant !== "") {
                throw new ConnectError("bad-tenant", "connect: the identity plane is asked for with no tenant named");
            }
            if (!planeLane(o.lane)) {
                throw new ConnectError("bad-tenant", "connect: the identity plane is asked for only on the oidc and github lanes");
            }
            tenant = PLANE;
        }
        else if (tenant !== "" && !validSlug(tenant)) {
            throw new ConnectError("bad-tenant", "connect: tenant is not a slug");
        }
        if (o.inbox !== undefined && !validInbox(o.inbox)) {
            throw new ConnectError("bad-inbox", "connect: inbox token too short or not a subject token");
        }
        if (typeof o.credential !== "function") {
            throw new ConnectError("no-credential", "connect: no credential");
        }
        this.sentinel = o.sentinel;
        this.lane = o.lane;
        this.tenant = tenant;
        this.inbox = o.inbox ?? newInbox();
        this.name = o.name;
        this.credential = o.credential;
        this.onError = o.onError;
    }
    /** token composes the connect token for the next connect, reading the credential. */
    token() {
        return { lane: this.lane, tenant: this.tenant, inbox: this.inbox, credential: this.credential() };
    }
    tokenString() {
        try {
            return formatToken(this.token());
        }
        catch (err) {
            this.onError?.(err instanceof Error ? err : new Error(String(err)));
            return "";
        }
    }
    /**
     * options are the connection options a placed connection uses: the sentinel
     * as its credential and the connect token as its token — nats-core composes
     * both into one CONNECT — its own inbox prefix, and reconnection without
     * end at the backoff's pace, so that expiry is a reconnect the caller does
     * not see and a connection dropped as soon as it is made is not a callout
     * every couple of seconds. Every call paces a connection of its own.
     */
    options() {
        const { jwt, seed } = this.sentinel;
        const opts = {
            authenticator: [
                jwtAuthenticator(() => jwt, () => seed),
                tokenAuthenticator(() => this.tokenString()),
            ],
            inboxPrefix: inboxPrefix(this.inbox),
            reconnect: true,
            maxReconnectAttempts: -1,
            ...new Backoff().options(),
        };
        if (this.name !== undefined) {
            opts.name = this.name;
        }
        return opts;
    }
    /**
     * connect places the connection through the connector at the servers. Extra
     * options are laid over the client's own.
     */
    connect(connector, servers, extra = {}) {
        return connector({ ...this.options(), ...extra, servers });
    }
}
/**
 * whoAmI asks the server who this connection is — the user-info request every
 * template admits — so a client can check the account it was placed in.
 */
export async function whoAmI(nc, timeout = 5000) {
    const msg = await nc.request(USER_INFO, undefined, { timeout });
    let reply;
    try {
        reply = JSON.parse(utf8Decode(msg.data));
    }
    catch (err) {
        throw new ConnectError("user-info", "connect: user info: reply is not JSON", { cause: err });
    }
    if (reply.error) {
        throw new ConnectError("user-info", `connect: user info: ${reply.error.description}`);
    }
    if (!reply.data) {
        throw new ConnectError("user-info", "connect: user info: no data");
    }
    const info = { user: reply.data.user, account: reply.data.account };
    if (reply.data.expires !== undefined) {
        info.expires = reply.data.expires;
    }
    if (reply.data.permissions !== undefined) {
        info.permissions = reply.data.permissions;
    }
    return info;
}
/**
 * checkPlacement asks who the connection is and refuses a placement in any
 * account but the one named — the tenant's, from its profile. The caller
 * closes the connection on a refusal, as the design says.
 */
export async function checkPlacement(nc, account, timeout) {
    const who = await whoAmI(nc, timeout);
    if (who.account !== account) {
        throw new ConnectError("placed-elsewhere", `connect: placed in ${who.account}, not ${account}`);
    }
    return who;
}
//# sourceMappingURL=connect.js.map